<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Alert,
  Button,
  Dialog,
  Dropdown,
  ScrollArea,
  Badge,
  toast,
  Sidebar,
  SidebarItem,
  SidebarLabel,
} from 'frappe-ui'
import {
  session,
  logout,
  isImpersonating,
  endImpersonation,
  roleHomeRoute,
  hasAnyRole,
  fetchBoot,
} from '../session'
import { errorMessage } from '../api'
import { navBySection, canAccessRoute } from '../rbac'
import type { NavItem, NavSection } from '../rbac'
import { leaveBadge, refreshLeaveBadge } from '../leave'
import { useTheme } from '../composables/useTheme'
import ProductOverlays from '../components/ProductOverlays.vue'
import { useProductFeed } from '../composables/useProductFeed'
import WorkspaceSwitcher from '../components/WorkspaceSwitcher.vue'
import BrandLogo from '../components/BrandLogo.vue'
import DirtyGuardDialog from '../components/DirtyGuardDialog.vue'
import TermsGate from '../components/TermsGate.vue'
import type { TermsStatus } from '../types/api'
import { workspace } from '../workspace'
import { dirtyGuardDialogState } from '../composables/useDirtyGuard'

const route = useRoute()
const router = useRouter()
const sections = computed(() => navBySection())
const forbidden = computed(
  () => session.boot.authenticated && !canAccessRoute(route),
)
const roleHome = computed(() => roleHomeRoute(session.boot))

const mobileOpen = ref(false)
const sidebarCollapsed = ref(
  localStorage.getItem('glamora_sidebar_collapsed') === 'true',
)
const endingImpersonation = ref(false)
const productOverlaysRef = ref<{
  startTourFromMenu: () => Promise<void>
} | null>(null)
const { preference, resolved, setTheme } = useTheme()
const { isNewFeature } = useProductFeed()

// Terms gate state
const termsGateDialogOpen = ref(false)
const graceBannerDismissed = ref(false)

/** Workspace label for the mobile top bar: "Salon" or "Salon · Branch". */
const workspaceTitle = computed(() => {
  const salon = session.boot?.company || 'Glamora'
  const branch = workspace.branch
  return branch ? `${salon} · ${branch}` : salon
})

/** True when the terms status blocks the app (state === 'required'). */
const termsBlocked = computed(() => session.boot.terms_status?.blocked === true)

/** True when in grace period and the user can accept (owner). */
const termsGrace = computed(
  () =>
    session.boot.terms_status?.state === 'grace' &&
    session.boot.terms_status?.can_accept === true,
)

/** True when terms are accepted or not applicable — no UI needed. */
const termsSettled = computed(() => {
  const ts = session.boot.terms_status
  return (
    !ts ||
    ts.state === 'accepted' ||
    ts.state === 'not_applicable' ||
    ts.state === 'disabled'
  )
})

/** Routes that should never be blocked by the terms gate. */
const unblockedRoutes = [
  '/login',
  '/signup',
  '/verify',
  '/forgot-password',
  '/reset-password',
]

/** True if the current route should bypass the terms gate. */
const isUnblockedRoute = computed(() =>
  unblockedRoutes.some((r) => route.path.startsWith(r)),
)

watch(
  () => route.fullPath,
  () => {
    mobileOpen.value = false
  },
)

/** Pending leave requests, for the owner and manager's "Leave requests" badge. */
const canDecideLeave = computed(() =>
  hasAnyRole(['Salon Owner', 'Salon Manager']),
)

function navBadge(item: NavItem): number {
  return item.badge === 'leaveOpen' ? (leaveBadge.openCount ?? 0) : 0
}

watch(
  [canDecideLeave, () => route.path],
  ([allowed]) => {
    if (allowed) void refreshLeaveBadge()
  },
  { immediate: true },
)

watch(sidebarCollapsed, (value) => {
  localStorage.setItem('glamora_sidebar_collapsed', String(value))
})

async function stopImpersonating() {
  endingImpersonation.value = true
  try {
    await endImpersonation()
    router.go(0)
  } catch (e) {
    toast.error(`Could not end the support session: ${errorMessage(e)}`)
  } finally {
    endingImpersonation.value = false
  }
}

function routeMatches(target: string, current: string) {
  if (target === '/' || target === '') return current === '/' || current === ''
  return current === target || current.startsWith(target + '/')
}

/**
 * Only the nav item with the longest matching route is active, so a nested
 * route (e.g. /finance/invoices) highlights "Invoices" and not also "Finance".
 */
const activeNavRoute = computed(() => {
  const current = route.path || '/'
  let best: string | null = null
  for (const section of sections.value) {
    for (const item of section.items) {
      const target = item.route || '/'
      if (routeMatches(target, current) && (best === null || target.length > best.length)) {
        best = target
      }
    }
  }
  return best
})

function isActive(path: string) {
  return (path || '/') === activeNavRoute.value
}

function toggleSidebar() {
  if (window.matchMedia('(min-width: 768px)').matches) {
    sidebarCollapsed.value = !sidebarCollapsed.value
  } else {
    mobileOpen.value = !mobileOpen.value
  }
}

function go(path: string) {
  router.push(path || '/')
  mobileOpen.value = false
}

async function onLogout() {
  try {
    await logout()
  } catch (e) {
    toast.warning(
      `Signed out on this device, but the server did not confirm it: ${errorMessage(e)}`,
    )
  }
  router.push({ name: 'Login' })
}

function openTermsGateDialog() {
  termsGateDialogOpen.value = true
}

function closeTermsGateDialog() {
  termsGateDialogOpen.value = false
}

function dismissGraceBanner() {
  graceBannerDismissed.value = true
}

function onTermsAccepted() {
  closeTermsGateDialog()
  // The global 417 hook will refresh the boot; we also force a refresh here
  // to immediately unblock the UI.
  fetchBoot()
}

const themeMenu = computed(() => [
  { label: 'Light', icon: 'lucide-sun', onClick: () => setTheme('light') },
  { label: 'Dark', icon: 'lucide-moon', onClick: () => setTheme('dark') },
  {
    label: 'System',
    icon: 'lucide-monitor',
    onClick: () => setTheme('system'),
  },
])

const userMenu = computed(() =>
  [
    {
      label: 'Account',
      icon: 'lucide-user',
      onClick: () => {
        void router.push('/account')
      },
    },
    session.boot?.is_staff
      ? {
          label: 'Navigation tour',
          icon: 'lucide-map',
          onClick: () => productOverlaysRef.value?.startTourFromMenu(),
        }
      : null,
    session.boot.can_access_desk
      ? {
          label: 'Open Desk',
          icon: 'lucide-external-link',
          onClick: () => window.open(session.boot.desk_url || '/app', '_blank'),
        }
      : null,
    { label: 'Log out', icon: 'lucide-log-out', onClick: onLogout },
  ].filter((item): item is NonNullable<typeof item> => item !== null),
)

const themeIcon = computed(() => {
  if (preference.value === 'system') return 'lucide-monitor'
  return resolved.value === 'dark' ? 'lucide-moon' : 'lucide-sun'
})
</script>

<template>
  <div
    class="flex min-h-screen w-full bg-surface-base text-ink-gray-9"
  >
    <!-- Mobile scrim -->
    <div
      v-if="mobileOpen"
      class="fixed inset-0 z-30 bg-black/50 md:hidden"
      aria-hidden="true"
      @click="mobileOpen = false"
    />

    <!--
      Opaque solid sidebar so nav links stay readable on mobile (no transparent overlay).
      Company switcher uses the frappe-ui Sidebar app-switcher pattern (toggler + chevrons).
    -->
    <!-- z-40 mobile drawer; dialogs use z-100 so the account dialog is never under the sidebar -->
    <aside
      class="fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col border-r border-outline-gray-1 shadow-xl transition-transform duration-200 md:sticky md:top-0 md:z-auto md:h-screen md:translate-x-0 md:self-start md:shadow-none"
      :class="[
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      ]"
    >
      <Sidebar
        v-model:collapsed="sidebarCollapsed"
        width="15rem"
        collapsed-width="3rem"
        class="!h-full !border-0"
      >
        <!-- Header: app identity + workspace switcher -->
        <div
          class="flex shrink-0 items-center gap-1 border-b border-outline-gray-1 bg-surface-gray-1 p-2"
        >
          <WorkspaceSwitcher />
          <Button
            class="md:hidden"
            variant="ghost"
            icon="lucide-x"
            label="Close"
            @click="mobileOpen = false"
          />
        </div>

        <ScrollArea
          class="min-h-0 flex-1 bg-surface-gray-1"
          viewport-class="px-2 pt-2 pb-10"
        >
          <div v-for="section in sections" :key="section.key" class="mb-3">
            <SidebarLabel>{{ section.label }}</SidebarLabel>
            <nav class="mt-0.5 space-y-0.5">
              <div
                v-for="item in section.items"
                :key="item.route"
                :data-tour-id="item.tourId || undefined"
                class="relative"
              >
                <SidebarItem
                  :icon="item.icon"
                  :label="item.label"
                  :active="isActive(item.route)"
                  @click="go(item.route)"
                >
                  <template v-if="navBadge(item) > 0" #suffix>
                    <Badge
                      class="mr-2"
                      :label="navBadge(item)"
                      :aria-label="`${navBadge(item)} pending`"
                      size="sm"
                      theme="amber"
                      variant="subtle"
                    />
                  </template>
                </SidebarItem>
                <Badge
                  v-if="item.featureKey && isNewFeature(item.featureKey)"
                  class="absolute right-2 top-1.5"
                  label="New"
                  size="sm"
                  theme="gray"
                  variant="solid"
                />
              </div>
            </nav>
          </div>
        </ScrollArea>

      </Sidebar>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <div
        v-if="isImpersonating()"
        class="flex shrink-0 items-center justify-between bg-surface-amber-2 px-4 py-2 text-sm text-ink-amber-3"
      >
        <div class="flex items-center gap-2">
          <span class="lucide-eye size-4" aria-hidden="true" />
          <span>
            Impersonating as
            <span class="font-medium">{{
              session.boot.full_name || session.boot.user
            }}</span>
          </span>
        </div>
        <Button
          size="sm"
          variant="subtle"
          label="Stop impersonating"
          :loading="endingImpersonation"
          @click="stopImpersonating"
        />
      </div>
      <header
        class="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-outline-gray-1 bg-surface-gray-1 px-3 md:px-4"
      >
        <!-- Left: nav controls + mobile title -->
        <div class="flex min-w-0 items-center gap-1">
          <Button
            variant="ghost"
            icon="lucide-menu"
            label="Toggle sidebar"
            class="md:hidden"
            @click="toggleSidebar"
          />
          <Button
            v-if="!sidebarCollapsed"
            class="hidden md:inline-flex"
            variant="ghost"
            icon="lucide-menu"
            label="Collapse sidebar"
            @click="sidebarCollapsed = true"
          />
          <Button
            v-else
            class="hidden md:inline-flex"
            variant="ghost"
            icon="lucide-menu"
            label="Expand sidebar"
            @click="sidebarCollapsed = false"
          />
          <div
            class="flex min-w-0 items-center gap-2 truncate text-sm font-medium text-ink-gray-8 md:hidden"
          >
            <BrandLogo :size="20" />
            {{ workspaceTitle }}
          </div>
        </div>

        <!-- Spacer keeps actions on the right on all breakpoints -->
        <div class="min-w-0 flex-1" aria-hidden="true" />

        <!-- Right: theme, settings, desk, logout -->
        <div class="flex shrink-0 items-center gap-1 sm:gap-2">
          <Dropdown :options="themeMenu">
            <Button variant="ghost" :icon="themeIcon" label="Theme" />
          </Dropdown>
          <Dropdown :options="userMenu">
            <Button variant="ghost" icon="lucide-user" label="User" />
          </Dropdown>
          <Button
            v-if="session.boot.can_access_desk"
            class="hidden sm:inline-flex"
            variant="ghost"
            icon-left="lucide-external-link"
            label="Desk"
            :link="session.boot.desk_url || '/app'"
          />
        </div>
      </header>

      <!-- Grace banner (dismissible per session) -->
      <div
        v-if="termsGrace && !graceBannerDismissed && !isUnblockedRoute"
        class="shrink-0 px-4 pt-3 md:px-6"
      >
        <div>
          <Alert
            theme="blue"
            title="Accept the platform fee terms"
            :description="`Accept the platform fee terms within ${session.boot.terms_status?.grace_days_left ?? 0} days to keep using POS, appointments and online booking.`"
            :dismissible="true"
            @update:modelValue="dismissGraceBanner"
          >
            <template #footer>
              <Button
                variant="solid"
                theme="blue"
                label="Review and accept"
                @click="openTermsGateDialog"
              />
            </template>
          </Alert>
        </div>
      </div>

      <main class="relative min-w-0 flex-1 p-4 md:p-6">
        <!-- Terms gate blocking screen -->
        <div
          v-if="termsBlocked && !isUnblockedRoute"
          class="flex h-full min-h-[60vh] items-center justify-center"
        >
          <div class="w-full max-w-2xl">
            <TermsGate
              :status="session.boot.terms_status as TermsStatus"
              @accepted="onTermsAccepted"
            />
          </div>
        </div>

        <!-- Normal page content -->
        <div v-else-if="!forbidden">
          <ProductOverlays ref="productOverlaysRef" class="mb-4" />
          <slot />
        </div>

        <!-- Forbidden screen -->
        <div
          v-else
          class="flex h-full min-h-[60vh] items-center justify-center"
        >
          <div
            class="flex flex-col items-center justify-center px-6 py-12 text-center"
          >
            <span
              class="lucide-lock size-8 text-ink-gray-4"
              aria-hidden="true"
            />
            <p class="mt-3 text-base font-medium text-ink-gray-9">
              You don't have access
            </p>
            <p class="mt-1 max-w-sm text-sm text-ink-gray-5">
              Your account doesn't have permission to view this page.
            </p>
            <Button
              class="mt-4"
              variant="solid"
              theme="gray"
              label="Go to my home"
              @click="router.push(roleHome)"
            />
          </div>
        </div>
      </main>
    </div>

    <!-- Terms gate dialog (for grace banner) -->
    <Dialog
      v-model:open="termsGateDialogOpen"
      title="Accept platform fee terms"
      size="lg"
    >
      <TermsGate
        :status="session.boot.terms_status as TermsStatus"
        dialog
        @accepted="onTermsAccepted"
        @close="closeTermsGateDialog"
      />
    </Dialog>

    <DirtyGuardDialog
      :modelValue="dirtyGuardDialogState.open.value"
      @update:modelValue="
        ($event) => (dirtyGuardDialogState.open.value = $event)
      "
    />
  </div>
</template>
